<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';
import IconCaretDownSmall from '@icons/ph_caret-down_small.svg';
import Icon from './Icon.vue';

type Opt = { label: string; value: string | number };

const props = defineProps<{
  modelValue: string | number | null;
  options: Opt[];
  placeholder?: string;
  customClass?: string;
}>();

const emit = defineEmits<{(e: 'update:modelValue', v: string | number | null): void;
  (e: 'change', v: string | number | null): void;
}>();

const isOpen = ref<boolean>(false);
const root = ref<HTMLElement | null>(null);

const selectedLabel = ref<string>();

const getInitialLabel = () => {
  const option = props.options.find((o) => o.value === props.modelValue);
  return option ? option.label : props.placeholder ?? '';
};

const toggle = (): void => {
  isOpen.value = !isOpen.value;
};

const select = (option: Opt): void => {
  emit('update:modelValue', option.value);
  emit('change', option.value);
  selectedLabel.value = option.label;
  isOpen.value = false;
};

const closeSelect = (e: MouseEvent): void => {
  if (root.value?.contains(e.target as Node)) {
    return;
  }

  isOpen.value = false;
};

selectedLabel.value = getInitialLabel();

onMounted(() => document.addEventListener('mousedown', closeSelect));
onBeforeUnmount(() => document.removeEventListener('mousedown', closeSelect));
</script>

<template>
  <div ref="root" class="ui-select" :class="customClass">
    <button type="button" class="ui-select__btn" @click="toggle">
      <span class="ui-select__label">{{ selectedLabel }}</span>
      <Icon :svg="IconCaretDownSmall" class="ui-select__chevron" />
    </button>
    <ul v-show="isOpen" class="ui-select__list">
      <li
        v-for="option in options"
        :key="String(option.value)"
        class="ui-select__item"
        :aria-selected="option.value === modelValue"
        @click="select(option)"
      >
        {{ option.label }}
      </li>
    </ul>
  </div>
</template>
